import React, { useState } from 'react';
import relatorioService from '../../services/relatorioPrestacaoContasService';
import PrestacaoPcArquivos from './PrestacaoPcArquivos';
import {
  bodyTextClass, labelTextClass, strongTextClass, subtleSurfaceClass, surfaceClass, type SectionProps,
} from './shared';

type Props = SectionProps & {
  declaraRh?: boolean | null;
  declaraAluguel?: boolean | null;
  /** Recarrega as PCs (flags e pendências de envio vêm do servidor). */
  onAlterado?: () => void;
};

const Pergunta: React.FC<{
  titulo: string; valor: boolean | null | undefined; desabilitado: boolean; onEscolher: (v: boolean) => void;
}> = ({ titulo, valor, desabilitado, onEscolher }) => (
  <div className="flex flex-wrap items-center gap-4">
    <span className={`text-sm font-semibold ${strongTextClass}`}>{titulo}</span>
    {[true, false].map((v) => (
      <label key={String(v)} className={`flex items-center gap-1.5 text-sm ${bodyTextClass}`}>
        <input type="radio" disabled={desabilitado} checked={valor === v} onChange={() => onEscolher(v)} />
        {v ? 'Sim' : 'Não'}
      </label>
    ))}
  </div>
);

/** Despesas com RH e aluguel — cobradas na 1ª PC (ou PC única). */
const PrestacaoDespesasRhAluguelSection: React.FC<Props> = ({
  pcId, podeEditar, declaraRh, declaraAluguel, onAlterado,
}) => {
  const [rh, setRh] = useState<boolean | null | undefined>(declaraRh);
  const [aluguel, setAluguel] = useState<boolean | null | undefined>(declaraAluguel);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const salvar = async (novoRh: boolean | null | undefined, novoAluguel: boolean | null | undefined) => {
    setRh(novoRh);
    setAluguel(novoAluguel);
    // O servidor só aceita a declaração completa; enquanto uma das respostas falta, só guardamos na tela.
    if (novoRh == null || novoAluguel == null) return;
    setSalvando(true);
    setErro(null);
    try {
      await relatorioService.salvarDeclaracaoDespesas(pcId, novoRh, novoAluguel);
      onAlterado?.();
    } catch (err: any) {
      setErro(err?.response?.data?.error || 'Falha ao salvar a declaração.');
    } finally {
      setSalvando(false);
    }
  };

  const declarado = declaraRh != null && declaraAluguel != null;

  return (
    <div className="space-y-5">
      <h2 className={`text-lg font-semibold ${strongTextClass}`}>Despesas com RH e aluguel</h2>
      <p className={`text-sm ${labelTextClass}`}>
        Cobradas na primeira PC (ou PC única). Informe se houve cada despesa; quando houve, o documento é obrigatório
        para enviar a PC.
      </p>

      <div className={`space-y-3 rounded-2xl border p-4 ${subtleSurfaceClass}`}>
        <Pergunta titulo="Houve despesa com RH?" valor={rh} desabilitado={!podeEditar || salvando}
          onEscolher={(v) => salvar(v, aluguel)} />
        <Pergunta titulo="Houve despesa com aluguel?" valor={aluguel} desabilitado={!podeEditar || salvando}
          onEscolher={(v) => salvar(rh, v)} />
        {erro && <p role="alert" className="text-sm text-red-600 dark:text-red-300">{erro}</p>}
      </div>

      {declarado && declaraRh && (
        <div className={`space-y-2 rounded-2xl border p-4 ${surfaceClass}`}>
          <h3 className={`font-medium ${strongTextClass}`}>Registro Funcional</h3>
          <p className={`text-xs ${labelTextClass}`}>
            Quando ocorrer despesas de RH — apresentar cópia simples de Registro Funcional (Decreto 325/2024, artigo 70, § 1º, alínea V).
          </p>
          <PrestacaoPcArquivos pcId={pcId} tipo="REGISTRO_FUNCIONAL" podeEditar={podeEditar}
            accept="application/pdf,image/*" botao="Anexar registro funcional" onChange={onAlterado} />
        </div>
      )}

      {declarado && declaraAluguel && (
        <div className={`space-y-2 rounded-2xl border p-4 ${surfaceClass}`}>
          <h3 className={`font-medium ${strongTextClass}`}>Contrato de Aluguel</h3>
          <p className={`text-xs ${labelTextClass}`}>
            Quando ocorrer despesas de aluguel — apresentar cópia do Contrato de Aluguel (Decreto 325/2024, artigo 70, § 1º, alínea IV).
          </p>
          <PrestacaoPcArquivos pcId={pcId} tipo="CONTRATO_ALUGUEL" podeEditar={podeEditar}
            accept="application/pdf,image/*" botao="Anexar contrato" onChange={onAlterado} />
        </div>
      )}
    </div>
  );
};

export default PrestacaoDespesasRhAluguelSection;
